import React, { useMemo } from "react";
import type { Machine } from "../services/BackendDataService";
import {
  METRIC_META,
  METRIC_ALIASES,
  type EnergyMetric,
} from "../services/energySensorMap";
import { usePanelData } from "../dashboard";
import {
  DATA_BINDING_VERSION,
  type ChannelSpec,
  type DataBinding,
  type ResolvedRange,
  type SourceSpec,
} from "../dashboard/spec";
import { DashboardSyncProvider } from "../dashboard/DashboardSyncContext";
import SeriesChart, {
  type SeriesDef,
  type AxisDef,
} from "../dashboard/panels/SeriesChart";
import WaitingState from "../dashboard/panels/WaitingState";
import ConnectionStatus from "../dashboard/panels/ConnectionStatus";
import EnergyGauge from "./EnergyGauge";
import MultiGauge from "./MultiGauge";
import { getMetricIcon } from "./metricIcons";

interface EnergyConditionMonitoringProps {
  machineData?: Machine | null;
  /** ISO-8601 start of the selected range; a value → Doris, undefined → live. */
  startTime?: string;
  paused?: boolean;
}

const METRIC_ORDER: EnergyMetric[] = [
  "electrical_power",
  "machine_speed",
  "ambient_temperature",
  "ambient_humidity",
  "compressed_air_pressure",
  "compressed_air_volume",
  "compressed_air_total",
  "vacuum_pressure",
  "vacuum_volume",
  "vacuum_total",
  "electricity_total",
];

// Channels for the shared data pipeline, derived from the energy metric catalog.
// The same alias lists drive both NATS-subject and Doris-sensor matching.
const ENERGY_CHANNELS: ChannelSpec[] = METRIC_ORDER.map((m) => ({
  key: m,
  label: METRIC_META[m].label,
  unit: METRIC_META[m].unit,
  color: METRIC_META[m].color,
  nats: { aliases: METRIC_ALIASES[m] },
  doris: { sensorAliases: METRIC_ALIASES[m] },
}));

// A chart group is one chart that may render several metrics as series (e.g.
// temperature + humidity together on two y-axes).
interface EnergyChartGroup {
  id: string;
  title: string;
  metrics: EnergyMetric[];
  seriesDefs: SeriesDef[];
  yAxes?: AxisDef[];
}

// Metrics that share a single chart. Each combined group renders one series per
// metric, each on its own y-axis (units differ, e.g. °C vs %).
const COMBINED_GROUPS: {
  id: string;
  title: string;
  metrics: EnergyMetric[];
  /** Mirror look: filled areas, second series on a reversed axis (fills from
   * the top) so pressure vs volume/rate don't overlap. */
  mirror?: boolean;
}[] = [
  {
    id: "climate",
    title: "Temperature & Humidity",
    metrics: ["ambient_temperature", "ambient_humidity"],
  },
  {
    id: "compressed_air",
    title: "Compressed Air",
    metrics: ["compressed_air_pressure", "compressed_air_volume"],
    mirror: true,
  },
  {
    id: "vacuum",
    title: "Vacuum",
    metrics: ["vacuum_pressure", "vacuum_volume"],
    mirror: true,
  },
];

const seriesDefFor = (m: EnergyMetric, axis?: number): SeriesDef => {
  const meta = METRIC_META[m];
  return {
    key: m,
    name: meta.label,
    unit: meta.unit,
    color: meta.color,
    type: "areaspline",
    showMarkers: true,
    ...(axis !== undefined ? { axis } : {}),
  };
};

// Stable chart layout, built once at module scope so series/axis object
// identities never change across renders (an inline array would rebuild the
// Highcharts options every ~400 ms flush and reset the chart). Combined groups
// are emitted at the position of their first metric; everything else is its own
// single-metric chart, preserving METRIC_ORDER.
// Monotonic accumulators: not charts, and not standalone KPI cards — each is
// folded into its "actual" gauge card as a Total footer (see GROUP_TOTAL_METRIC).
const TOTAL_METRICS: ReadonlySet<EnergyMetric> = new Set([
  "electricity_total",
  "compressed_air_total",
  "vacuum_total",
]);

// Pairs a KPI group (by id) with the total accumulator shown in its footer.
const GROUP_TOTAL_METRIC: Record<string, EnergyMetric> = {
  electrical_power: "electricity_total",
  compressed_air: "compressed_air_total",
  vacuum: "vacuum_total",
};

// Chart order — independent of METRIC_ORDER/gauge order: climate, then
// machine speed, then compressed air, then vacuum, with electrical power
// last (gauges keep the full METRIC_ORDER; this only reorders the charts).
const CHART_METRIC_ORDER: EnergyMetric[] = [
  "ambient_temperature",
  "ambient_humidity",
  "machine_speed",
  "compressed_air_pressure",
  "compressed_air_volume",
  "vacuum_pressure",
  "vacuum_volume",
  "electrical_power",
];

const ENERGY_CHART_GROUPS: EnergyChartGroup[] = (() => {
  const groups: EnergyChartGroup[] = [];
  const emitted = new Set<string>();
  for (const m of CHART_METRIC_ORDER) {
    const combined = COMBINED_GROUPS.find((g) => g.metrics.includes(m));
    if (combined) {
      if (emitted.has(combined.id)) continue;
      emitted.add(combined.id);
      const mirror = combined.mirror ?? false;
      groups.push({
        id: combined.id,
        title: combined.title,
        metrics: combined.metrics,
        // Mirror groups (air/vacuum) render both metrics as filled
        // areasplines sharing one zero baseline (see yAxes below) rather than
        // two independent scales. Non-mirror groups (climate) stay as lines
        // so each axis auto-scales cleanly without overlapping fills.
        seriesDefs: combined.metrics.map((gm, i) => ({
          ...seriesDefFor(gm, i),
          type: mirror ? "areaspline" : "line",
        })),
        yAxes: combined.metrics.map((gm, i) => {
          const meta = METRIC_META[gm];
          // A metric whose range sits at/below zero (e.g. vacuum pressure,
          // min < 0) has its zero at the range's *top*; reversing that axis
          // moves zero to the bottom instead. Combined with pinning zero as
          // the boundary for every axis (min 0 for a non-negative metric,
          // max 0 for a negative one when no explicit bound is set), zero
          // always lands on the plot's bottom edge — the one edge every axis
          // shares — so the chart shows a single zero baseline instead of
          // each axis floating its own.
          const isNegative = meta.min !== undefined && meta.min < 0;
          return {
            title: meta.unit,
            color: meta.color,
            opposite: i > 0,
            reversed: isNegative,
            softMin: meta.min ?? (isNegative ? undefined : 0),
            softMax: meta.max ?? (isNegative ? 0 : undefined),
          };
        }),
      });
    } else {
      const meta = METRIC_META[m];
      groups.push({
        id: m,
        title: `${meta.label} (${meta.unit})`,
        metrics: [m],
        seriesDefs: [seriesDefFor(m)],
        // A single soft-ranged axis so even a flat series shows a scale.
        yAxes: [{ color: meta.color, softMin: meta.min, softMax: meta.max }],
      });
    }
  }
  return groups;
})();

// KPI (gauge) groups mirror the chart grouping: combined metrics share one card
// as a double gauge. Built from the full METRIC_ORDER (totals included, since
// totals stay as KPIs) and tagged with their primary/system section.
interface EnergyKpiGroup {
  id: string;
  title?: string;
  metrics: EnergyMetric[];
}

const ENERGY_KPI_GROUPS: EnergyKpiGroup[] = (() => {
  const groups: EnergyKpiGroup[] = [];
  const emitted = new Set<string>();
  for (const m of METRIC_ORDER) {
    // Totals aren't their own cards; they're folded into their actual gauge.
    if (TOTAL_METRICS.has(m)) continue;
    const combined = COMBINED_GROUPS.find((g) => g.metrics.includes(m));
    if (combined) {
      if (emitted.has(combined.id)) continue;
      emitted.add(combined.id);
      groups.push({
        id: combined.id,
        title: combined.title,
        metrics: combined.metrics,
      });
    } else {
      groups.push({ id: m, metrics: [m] });
    }
  }
  // KPI card order only (independent of METRIC_ORDER/chart order): swap
  // "Temperature & Humidity" and "Electrical Power" so climate leads.
  const climateIdx = groups.findIndex((g) => g.id === "climate");
  const powerIdx = groups.findIndex((g) => g.id === "electrical_power");
  if (climateIdx !== -1 && powerIdx !== -1) {
    [groups[climateIdx], groups[powerIdx]] = [
      groups[powerIdx],
      groups[climateIdx],
    ];
  }
  return groups;
})();

const EnergyConditionMonitoring: React.FC<EnergyConditionMonitoringProps> = ({
  machineData,
  startTime,
}) => {
  const machineBK =
    machineData?.maschinennummer || machineData?.maschineBK || "";

  // Source follows the range (energy rule): live → NATS, date range → Doris.
  // Hybrid was tried for live (Doris history + NATS tail) and reverted: energy
  // channels only declare `sensorAliases`, no Doris `prefix`, so
  // dorisPrefixes() falls back to '%' and queries every sensor on the
  // machine — when that broad, slow query resolves it sends a `reset` patch
  // that wipes the live points already accumulated, making the visible window
  // shrink instead of grow. Plain NATS has no such reset, so live data just
  // accumulates from connect time onward.
  const binding = useMemo<DataBinding>(
    () => ({
      version: DATA_BINDING_VERSION,
      machineRef: machineBK,
      source: (startTime ? { mode: "doris" } : { mode: "nats" }) as SourceSpec,
      channels: ENERGY_CHANNELS,
    }),
    [machineBK, startTime],
  );

  const range = useMemo<ResolvedRange>(
    () =>
      startTime
        ? { kind: "window", startMs: Date.parse(startTime), endMs: Date.now() }
        : { kind: "live" },
    [startTime],
  );

  // The shared data layer — one subscription (NATS live or Doris history).
  const { series, latest, lastUpdate, loading, loadDetail } = usePanelData(
    binding,
    range,
  );

  const connected = latest.size > 0;
  // A Doris window that simply contains no rows is a finished query, not a
  // pending one. Without this the screen sat on "Loading history…" forever and
  // an empty range looked identical to a hang.
  const emptyResult = !connected && !loading && !!startTime;
  const shownMetrics = useMemo(
    () =>
      METRIC_ORDER.filter(
        (m) => (series.get(m)?.length ?? 0) > 0 || latest.has(m),
      ),
    [series, latest],
  );
  const shownGroups = ENERGY_KPI_GROUPS.filter((g) =>
    g.metrics.some((m) => shownMetrics.includes(m)),
  );

  if (!machineBK) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
        No machine selected.
      </div>
    );
  }

  // Total accumulator paired with a KPI group, shown as its footer (or null).
  const totalFooter = (groupId: string) => {
    const tm = GROUP_TOTAL_METRIC[groupId];
    if (!tm) return undefined;
    return {
      value: latest.get(tm) ?? 0,
      unit: METRIC_META[tm].unit,
    };
  };

  // Single-metric groups render one gauge card; combined groups render one
  // multi-ring gauge (mirrors the combined charts). A paired total accumulator
  // is folded in as a footer instead of a separate card.
  const renderGauges = (groups: EnergyKpiGroup[]) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr">
      {groups.map((g) =>
        g.metrics.length === 1 ? (
          (() => {
            const m = g.metrics[0];
            const meta = METRIC_META[m];
            return (
              <EnergyGauge
                key={g.id}
                title={meta.label}
                value={latest.get(m) ?? 0}
                unit={meta.unit}
                min={meta.min}
                max={meta.max}
                points={series.get(m)}
                lastUpdate={lastUpdate.get(m)}
                color={meta.color}
                icon={getMetricIcon(m)}
                total={totalFooter(g.id)}
              />
            );
          })()
        ) : (
          <MultiGauge
            key={g.id}
            title={g.title ?? ""}
            icon={getMetricIcon(g.id)}
            total={totalFooter(g.id)}
            items={g.metrics.map((m) => {
              const meta = METRIC_META[m];
              return {
                label: meta.label,
                value: latest.get(m) ?? 0,
                unit: meta.unit,
                min: meta.min,
                max: meta.max,
                points: series.get(m),
                lastUpdate: lastUpdate.get(m),
                color: meta.color,
              };
            })}
          />
        ),
      )}
    </div>
  );

  return (
    <DashboardSyncProvider>
      <div className="space-y-8">
        {/* Connection status */}
        <ConnectionStatus
          connected={connected}
          label={
            connected
              ? `${startTime ? "History" : "Live data"} connected · ${shownMetrics.length} metric${shownMetrics.length === 1 ? "" : "s"}`
              : emptyResult
                ? "No energy data in the selected range"
                : "Waiting for energy data…"
          }
          className="mb-4"
        />

        {!connected && (
          <WaitingState
            title={
              emptyResult
                ? "No energy data in the selected range"
                : startTime
                  ? "Loading history…"
                  : "Listening for energy signals…"
            }
            subject={startTime ? undefined : `edge-data.live.*.${machineBK}.>`}
            detail={
              emptyResult
                ? "The query succeeded but this machine has no sensor readings in this window. Sensor history can lag behind production data — try a wider range, or a custom date further back."
                : undefined
            }
          />
        )}

        {/* KPIs — one uniform grid, no metric-type grouping */}
        {shownGroups.length > 0 && renderGauges(shownGroups)}

        {/* Charts — one per group (some combine metrics, e.g. temp + humidity),
            all linked via the sync provider */}
        {ENERGY_CHART_GROUPS.map((group) => {
          const anyShown = group.metrics.some((m) => shownMetrics.includes(m));
          if (!anyShown) return null;
          return (
            <div key={group.id} className="space-y-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <div className="h-96">
                  <SeriesChart
                    title={group.title}
                    seriesDefs={group.seriesDefs}
                    yAxes={group.yAxes}
                    data={series}
                    height={375}
                    canDetail={!!startTime}
                    onDetail={loadDetail}
                    commentType={`energy_${group.id}`}
                    machineBK={machineBK}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </DashboardSyncProvider>
  );
};

export default EnergyConditionMonitoring;
